import React from 'react';
import { useTranslation } from 'react-i18next';
import { Identity } from 'models';
import { useForm } from '@weezevent/weezjs-core';

import { Button, DropdownLight, Message, Input, InputLabel, Modal, ToastContext } from '@weezevent/nacre';
import css from './identities.module.css';

const IdentityModal = ({ organizationId, identity, syncIdentities, open, onClose, domains, isIdentityAdmin, isIdentityManager }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const [displayWarning, setDisplayWarning] = React.useState(false);

    const availableDomains = domains?.filter(d => d.verification_status === 'success' || d.verification_status === 'pending') || [];

    const customDomains = availableDomains.filter(d => d.name !== 'weezevent.com');

    const capacityDomains = isIdentityAdmin ? availableDomains : isIdentityManager && customDomains.length > 0 ? customDomains : [];

    const isInputDisabled = capacityDomains.length === 0;

    const domainsOptions = capacityDomains.length > 0 ? capacityDomains.map((d, i) => d.toOptions(i)) : [{ label: '@weezevent.com', value: 1 }];

    const fromAddress = identity?.from_address;
    const replyTo = identity?.reply_to;

    const senderEmailLocal = typeof fromAddress?.email === 'string' && fromAddress.email.includes('@') ? fromAddress.email.split('@')[0] : '';

    const [form, setForm] = useForm({
        sender_name: fromAddress?.name ?? '',
        sender_email_local: senderEmailLocal,
        domain_value: 1,
        reply_name: replyTo?.name ?? 'Support',
        reply_email: replyTo?.email ?? 'support@weezevent.com',
        organization: null,
        media: 'email'
    });
    const currentDomain = domainsOptions[form.domain_value - 1];
    const fullSenderEmail = `${form.sender_email_local}${currentDomain.label}`;

    const handleSelect = value => {
        setForm('domain_value', value);
        const selected = domainsOptions[value - 1];
        setDisplayWarning(selected.status === 'pending');
    };

    const handleSubmit = React.useCallback(() => {
        const payload = {
            from_address: {
                name: form.sender_name,
                email: fullSenderEmail
            },
            reply_to: {
                name: form.reply_name || 'Support',
                email: form.reply_email || 'support@weezevent.com'
            },
            organization: organizationId,
            media: 'email'
        };

        const newIdentity = new Identity({ ...payload, id: identity?.id || null });

        return newIdentity
            .save({ organizationId })
            .then(response => {
                if ([201, 200].includes(response.status)) {
                    toast.success(t('sauron.toasts.identities.create-identity-success'));
                    syncIdentities();
                }
                onClose();
            })
            .catch(() => toast.error(t('sauron.toasts.error')));
    }, [form, organizationId, fullSenderEmail]);

    return (
        <Modal
            size="large"
            open={open}
            allowExternalClosing={true}
            onClose={onClose}
            title={t('sauron.emails.identities.create.title')}
            subtitle={t('sauron.emails.identities.create.subtitle')}
            customFooter={
                <Button.Group>
                    <Button onClick={onClose} label={t('common.cta.cancel')} />
                    <Button primary inverted onClick={handleSubmit} disabled={!form.sender_name || !form.sender_email_local} label={t('common.cta.save')} />
                </Button.Group>
            }
        >
            <div className={css['idendity-input-container']}>
                <div className={css['identity-input']}>
                    <div className={css['input-wrapper']}>
                        <InputLabel label={t('sauron.emails.identities.create.sender.address')} />
                        <div className={css['identity-input-email']}>
                            <span>
                                <Input
                                    className={css['identity-input-email-locale']}
                                    placeholder={t('sauron.emails.identities.create.sender.placeholder.address')}
                                    value={form.sender_email_local}
                                    disabled={isInputDisabled}
                                    onChange={({ target: { value } }) => setForm('sender_email_local', value)}
                                />
                                <DropdownLight
                                    className={css['identity-input-domain']}
                                    onChange={value => handleSelect(value)}
                                    value={form.domain_value}
                                    items={domainsOptions}
                                    disabled={isInputDisabled}
                                />
                            </span>
                        </div>
                    </div>
                    {displayWarning && (
                        <Message type="warning" title={t('sauron.emails.identities.create.message-pending-domain.title')}>
                            {t('sauron.emails.identities.create.message-pending-domain.content')}
                        </Message>
                    )}
                </div>

                <div className={css['identity-input']}>
                    <div className={css['input-wrapper']}>
                        <InputLabel label={t('sauron.emails.identities.create.sender.name')} />
                        <Input
                            placeholder={t('sauron.emails.identities.create.sender.placeholder.name')}
                            value={form.sender_name}
                            onChange={({ target: { value } }) => setForm('sender_name', value)}
                        />
                    </div>
                </div>

                <div className={css['identity-input']}>
                    <div className={css['input-wrapper']}>
                        <InputLabel label={t('sauron.emails.identities.create.reply-to.name')} />
                        <Input
                            placeholder={t('sauron.emails.identities.create.reply-to.placeholder.name')}
                            value={form.reply_name}
                            onChange={({ target: { value } }) => setForm('reply_name', value)}
                        />
                    </div>
                </div>

                <div className={css['identity-input']}>
                    <div className={css['input-wrapper']}>
                        <InputLabel label={t('sauron.emails.identities.create.reply-to.address')} />
                        <Input
                            placeholder={t('sauron.emails.identities.create.reply-to.placeholder.address')}
                            value={form.reply_email}
                            onChange={({ target: { value } }) => setForm('reply_email', value)}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default IdentityModal;
